import React, { useCallback, useEffect, useMemo, useRef } from 'react'
import styled, { css } from 'styled-components'
import { useIntl } from 'gatsby-plugin-intl'
import { useDispatch, useSelector } from 'react-redux'
import { breakpoint } from 'src/theme/grid'
import Loader from 'src/components/LinearLoader'
import { API_STATES } from 'src/ducks/consts'
import Header from './components/ConversationHeader'
import TextInput from './components/ConversationInput'
import MessagesListComponent from './components/MessagesList'
import { selectChatSessionById } from './duck/selectors'
import {
  bootstrapConversationRoutine,
  sendMessageRoutine,
  markMessageAsSeenRoutine,
  updateChatSessionUi,
  getConversationMessagesRoutine,
  muteConversationRoutine,
  unmuteConversationRoutine,
  reserveOfferRoutine,
  acceptReservationRoutine,
  rejectReservationRoutine,
  cancelReservationRoutine,
  getContextAvailableActionsRoutine
} from './duck/actions'
import { Conversation as ConversationRecord } from './duck/records'
import { CHAT_SESSION_STATUS } from './duck/consts'
import usePageActivity from './components/usePageActivity'

const Conversation = ({
  chatId,
  isActive = false,
  onMinimize,
  onClose,
  onFocus,
  interlocutorOnline
}) => {
  const dispatch = useDispatch()
  const intl = useIntl()
  const messagesRef = useRef(null)
  const bootstrapRequestKeyRef = useRef(null)
  const pageActive = usePageActivity()

  const session = useSelector(
    state => selectChatSessionById(state, chatId) || ConversationRecord()
  )
  const lastRequest = useSelector(state => state.messages.lastRequest || {})

  const defaultInterlocutor = useMemo(
    () => ConversationRecord().interlocutor,
    []
  )

  const isConversationMuted = session?.isConversationMuted
  const messages = session?.messages || []
  const conversationId = session?.conversationId
  const isOpen = session?.status === CHAT_SESSION_STATUS.OPEN
  const isConversationAvailable = session?.isAvailable !== false
  const isConversationLoading =
    session?.messagesState === API_STATES.IN_PROGRESS
  const isConversationContextLoading =
    session?.contextState === API_STATES.IN_PROGRESS
  const conversationContextActions = session?.contextActions || {}
  const interlocutor = session?.interlocutor || defaultInterlocutor
  const awaitingReadReceipt = useMemo(
    () =>
      messages.reduce(
        (awaiting, group) =>
          group.reduce((pending, message) => {
            if (
              message.isInterlocutorMessage ||
              message.isSystemMessage ||
              message.sendStatus !== 'sent'
            ) {
              return pending
            }
            return !message.seenAt
          }, awaiting),
        false
      ),
    [messages]
  )

  useEffect(() => {
    if (!isActive || !pageActive || !conversationId || !awaitingReadReceipt) {
      return undefined
    }
    const refreshReceipts = () =>
      dispatch(
        getConversationMessagesRoutine.trigger({
          chatId,
          conversationId,
          refresh: true,
          values: { limit: 10 }
        })
      )
    refreshReceipts()
    const interval = setInterval(refreshReceipts, 15000)
    return () => clearInterval(interval)
  }, [
    dispatch,
    chatId,
    conversationId,
    awaitingReadReceipt,
    isActive,
    pageActive
  ])

  useEffect(() => {
    if (!isOpen) return
    if (
      session?.conversationId &&
      session?.messagesState !== API_STATES.PRISTINE
    ) {
      return
    }
    const hasBootstrapData = Boolean(
      session?.conversationId || session?.receiverId || session?.contextId
    )
    if (!hasBootstrapData) return

    const bootstrapRequestKey = [
      chatId,
      session?.receiverId || '',
      session?.contextType || '',
      session?.contextId || ''
    ].join('|')

    if (bootstrapRequestKeyRef.current === bootstrapRequestKey) return
    bootstrapRequestKeyRef.current = bootstrapRequestKey

    dispatch(
      bootstrapConversationRoutine.trigger({
        chatId,
        receiverId: session?.receiverId,
        contextId: session?.contextId,
        contextType: session?.contextType
      })
    )
  }, [
    dispatch,
    chatId,
    session?.receiverId,
    session?.contextId,
    session?.contextType,
    session?.conversationId,
    isOpen
  ])

  const scrollDown = useCallback(() => {
    const containerEl = messagesRef.current?.parentElement
    if (!containerEl) return
    containerEl.scrollTo({ top: containerEl.scrollHeight, behavior: 'smooth' })
  }, [])

  const handleSendMessage = useCallback(
    ({ message, clientId, callback, onError }) => {
      dispatch(
        sendMessageRoutine.trigger({
          chatId,
          message,
          clientId,
          onError,
          callback: () => {
            if (typeof callback === 'function') {
              callback()
            }
          }
        })
      )
      if (typeof window !== 'undefined') {
        window.requestAnimationFrame(scrollDown)
      }
    },
    [dispatch, chatId, scrollDown]
  )

  const handleRetry = useCallback(
    message => {
      handleSendMessage({ message: message.text, clientId: message.clientId })
    },
    [handleSendMessage]
  )

  const saveScrollPosition = useCallback(
    scrollPosition => {
      dispatch(updateChatSessionUi({ chatId, scrollPosition }))
    },
    [dispatch, chatId]
  )

  const renderInputWrapper = () => {
    if (isConversationAvailable) {
      return (
        <InputWrapper>
          <TextInput
            name={`message-${chatId}`}
            id={`message-${chatId}`}
            value={session.draft}
            disabled={isConversationLoading && messages.length === 0}
            onChange={draft => dispatch(updateChatSessionUi({ chatId, draft }))}
            onSubmit={value => handleSendMessage({ message: value })}
            placeholder={intl.formatMessage({
              id: 'messages.conversationInputPlaceholder'
            })}
          />
        </InputWrapper>
      )
    }

    return (
      <InputWrapper>
        <TextInput
          disabled
          placeholder={intl.formatMessage({
            id: 'messages.conversationDisabledInputPlaceholder'
          })}
        />
      </InputWrapper>
    )
  }

  const handleMarkMessageAsSeen = useCallback(
    payload =>
      dispatch(markMessageAsSeenRoutine.trigger({ chatId, ...payload })),
    [dispatch, chatId]
  )

  const handleGetMessages = useCallback(
    payload => {
      if (!conversationId) return
      dispatch(
        getConversationMessagesRoutine.trigger({
          chatId,
          conversationId,
          ...payload
        })
      )
    },
    [dispatch, chatId, conversationId]
  )

  const handleGetContextActions = convoId => {
    if (!convoId && !conversationId) return
    dispatch(
      getContextAvailableActionsRoutine.trigger({
        chatId,
        conversationId: convoId || conversationId
      })
    )
  }

  const handleReserveOffer = payload =>
    dispatch(reserveOfferRoutine.trigger(payload))

  const handleAcceptReservation = payload =>
    dispatch(acceptReservationRoutine.trigger(payload))

  const handleRejectReservation = payload =>
    dispatch(rejectReservationRoutine.trigger(payload))

  const handleCancelReservation = payload =>
    dispatch(cancelReservationRoutine.trigger(payload))

  const handleToggleMute = () => {
    if (isConversationMuted) {
      dispatch(unmuteConversationRoutine.trigger({ chatId }))
    } else {
      dispatch(muteConversationRoutine.trigger({ chatId }))
    }
  }

  const handleFocus = () => {
    if (!isActive && typeof onFocus === 'function') {
      onFocus()
    }
  }

  return (
    <Container
      onMouseDown={handleFocus}
      onFocusCapture={handleFocus}
      $isActive={isActive}
    >
      <LoaderStyled loading={isConversationLoading} />
      <Header
        interlocutor={interlocutor}
        interlocutorOnline={interlocutorOnline}
        conversation={session}
        isConversationMuted={isConversationMuted}
        toggleConversationNotifications={handleToggleMute}
        reserveOffer={handleReserveOffer}
        acceptReservation={handleAcceptReservation}
        rejectReservation={handleRejectReservation}
        cancelReservation={handleCancelReservation}
        getContextAvailableActions={handleGetContextActions}
        isConversationContextLoading={isConversationContextLoading}
        conversationContextActions={conversationContextActions}
        onClose={onClose}
        onMinimize={onMinimize}
        isActive={isActive}
      />
      <MessagesArea>
        <MessagesList
          lastRequest={lastRequest}
          messagesRef={messagesRef}
          getMessages={handleGetMessages}
          messages={messages}
          messagesState={session?.messagesState}
          newMessagesLabel={intl.formatMessage({ id: 'messages.newMessages' })}
          canRead={isActive && pageActive}
          scrollPosition={session.scrollPosition}
          onScrollPositionChange={saveScrollPosition}
          onRetry={handleRetry}
          markMessageAsSeen={handleMarkMessageAsSeen}
          conversationId={conversationId}
          endOfChatMessage={
            isConversationAvailable
              ? null
              : intl.formatMessage({
                  id: 'messages.conversationFinishMessage'
                })
          }
        />
      </MessagesArea>
      {renderInputWrapper()}
    </Container>
  )
}

export default Conversation

const Container = styled.div`
  display: flex;
  flex-direction: column;
  box-sizing: border-box;
  background-color: white;
  border-radius: 8px 8px 0 0;
  border: 1px solid ${({ theme }) => theme.colors.ghost.hex()};
  box-shadow: 0 12px 32px rgba(24, 27, 34, 0.18);
  width: 340px;
  min-width: 340px;
  max-width: 340px;
  height: 410px;
  min-height: 410px;
  max-height: 410px;
  overflow: hidden;
  position: relative;

  ${({ $isActive, theme }) =>
    $isActive &&
    css`
      border-color: ${theme.colors.easternBlue.hex()};
      box-shadow: 0 16px 36px rgba(24, 27, 34, 0.26);
    `}

  ${breakpoint.s`
    height: min(410px, calc(100vh - 80px));
    min-height: min(410px, calc(100vh - 80px));
    max-height: min(410px, calc(100vh - 80px));
  `}

  @media (max-width: 666px) {
    width: 100%;
    min-width: 0;
    max-width: 340px;
    height: min(410px, calc(100vh - 32px));
    min-height: min(410px, calc(100vh - 32px));
    max-height: min(410px, calc(100vh - 32px));
  }

  @media (max-width: 479px) {
    width: 100vw;
    max-width: 100vw;
    border-radius: 0;
    border-left: none;
    border-right: none;
    box-shadow: none;
    height: min(410px, calc(100vh - 16px));
    min-height: min(410px, calc(100vh - 16px));
    max-height: min(410px, calc(100vh - 16px));
  }
`

const InputWrapper = styled.div`
  display: block;
  width: 100%;
  padding: 0 1rem 0.8rem 1rem;
  box-sizing: border-box;
  flex-shrink: 0;
  z-index: 12;
`

const LoaderStyled = styled(Loader)`
  position: absolute;
  top: 0;
  left: 0;
`

const MessagesArea = styled.div`
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
`

const MessagesList = styled(MessagesListComponent)`
  flex: 1;
  min-height: 0;
  width: 100%;
`
